<template>
  <div>
    <v-menu offset-y :close-on-content-click="false" max-width="420">
      <template v-slot:activator="menu">
        <slot name="activator" v-bind="menu">
          <v-tooltip bottom>
            <template v-slot:activator="tooltip">
              <v-btn icon class="mr-2" v-on="{ ...menu.on, ...tooltip.on }">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $i18n.translate(menuLabel) }}</span>
          </v-tooltip>
        </slot>
      </template>

      <v-card min-width="400">
        <v-card-subtitle class="py-2">
          <slot name="title">
            <span class="subtitle-2">{{ $i18n.translate(title) }}</span>
          </slot>

          <v-btn small color="primary" class="ml-2" @click="onApply" v-if="!applyOnChange">{{
            $i18n.translate("Apply")
          }}</v-btn>
        </v-card-subtitle>
        <v-spacer></v-spacer>
        <v-card-text>
          <v-form ref="form" lazy-validation>
            <div v-for="(f, idx) in schema" :key="f.key || idx" class="mb-3">
              <slot
                name="field"
                :field="f"
                :value="localFilters[f.key]"
                :set="v => updateField(f.key, v)"
                :menu="rangesMenu[f.key]"
                :temp="tempRange[f.key]"
              >
                <div v-if="f.type === 'daterange'" class="d-flex" style="gap: 8px;">
                  <template v-if="rangesMenu[f.key] && localFilters[f.key] && tempRange[f.key]">
                    <v-menu v-model="rangesMenu[f.key].from" :close-on-content-click="false" offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          dense
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          :label="$i18n.translate(f.fromLabel || 'From')"
                          v-model="localFilters[f.key].from"
                          @click:clear="clearDate(f.key, 'from')"
                        />
                      </template>
                      <v-date-picker v-model="tempRange[f.key].from" @input="setDate(f.key, 'from')" />
                    </v-menu>

                    <v-menu v-model="rangesMenu[f.key].to" :close-on-content-click="false" offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          dense
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          :label="$i18n.translate(f.toLabel || 'To')"
                          v-model="localFilters[f.key].to"
                          @click:clear="clearDate(f.key, 'to')"
                        />
                      </template>
                      <v-date-picker v-model="tempRange[f.key].to" @input="setDate(f.key, 'to')" />
                    </v-menu>
                  </template>
                </div>

                <v-select
                  v-else-if="f.type === 'select' || f.type === 'multiselect'"
                  dense
                  :label="$i18n.translate(f.label || f.key)"
                  :items="f.items"
                  :multiple="f.type === 'multiselect'"
                  :chips="f.type === 'multiselect'"
                  :small-chips="f.type === 'multiselect'"
                  item-text="text"
                  item-value="value"
                  v-model="localFilters[f.key]"
                />

                <v-select
                  v-else-if="f.type === 'effective'"
                  dense
                  :label="$i18n.translate(f.label || f.key)"
                  :items="f.items || defaultEffectiveItems"
                  item-text="text"
                  item-value="value"
                  v-model="localFilters[f.key]"
                />

                <CourseField
                  v-else-if="f.type === 'byCourse'"
                  showKey
                  v-model="localFilters[f.key]"
                  :label="$i18n.translate(f.label || f.key)"
                />

                <v-text-field
                  v-else
                  :label="$i18n.translate(f.label || f.key)"
                  dense
                  hide-details="auto"
                  v-model="localFilters[f.key]"
                  v-bind="f.props"
                >
                </v-text-field>
              </slot>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import CourseField from "../../fields/CourseField.vue";
export default {
  components: { CourseField },
  name: "TableFilters",
  props: {
    value: { type: Object, default: () => ({}) },

    schema: { type: Array, required: true },

    title: { type: String, default: "Advanced Search" },
    menuLabel: { type: String, default: "Advanced Search" },

    applyOnChange: { type: Boolean, default: false }
  },

  data() {
    return {
      localFilters: {},
      rangesMenu: {},
      tempRange: {},
      isSyncing: false
    };
  },

  computed: {
    defaultEffectiveItems() {
      return [
        { text: this.$i18n.translate("Active"), value: true },
        { text: this.$i18n.translate("Inactive"), value: false },
        { text: this.$i18n.translate("All"), value: null }
      ];
    },

    normalizedFilters() {
      return this.normalizeFilters(this.localFilters);
    },

    activeChips() {
      const chips = [];
      for (const f of this.schema) {
        if (!(f.key in this.normalizedFilters)) continue;

        const val = this.normalizedFilters[f.key];
        let text = "";
        if (f.type === "multiselect" && Array.isArray(val)) {
          text = val.map(v => this.valueToText(f, v)).join(", ");
        } else if (f.type === "effective") {
          text = val === true ? this.$i18n.translate("Active") : this.$i18n.translate("Inactive");
        } else if (f.type === "daterange" && val && (val.from || val.to)) {
          text = [val.from, val.to].filter(Boolean).join(" → ");
        } else if (f.type === "byCourse" && val && val.name) {
          text = val.name;
        } else {
          text = this.valueToText(f, val);
        }

        chips.push({ key: f.key, label: f.label || f.key, text, value: val });
      }
      return chips;
    }
  },

  created() {
    this.syncFromProp();
  },

  watch: {
    schema: {
      deep: true,
      handler() {
        this.syncFromProp();
      }
    },
    value: {
      deep: true,
      handler() {
        this.syncFromProp();
        if (this.applyOnChange) this.onApply();
      }
    },

    localFilters: {
      deep: true,
      handler(n) {
        if (this.isSyncing) return;
        if (this.sameAsProp(n, this.value)) return;
        this.$emit("input", n);
      }
    }
  },

  methods: {
    sameAsProp(a, b) {
      return JSON.stringify(a) === JSON.stringify(b);
    },

    valueToText(f, v) {
      if (f.items && Array.isArray(f.items)) {
        const found = f.items.find(it => String(it.value) === String(v));
        return found ? this.$i18n.translate(found.text) : String(v);
      }
      return String(v);
    },

    setDate(key, side) {
      const next = Object.assign({}, this.localFilters[key] || {});
      next[side] = this.tempRange[key][side];
      this.$set(this.localFilters, key, next);
      this.$set(this.rangesMenu[key], side, false);
    },

    clearDate(key, side) {
      const next = Object.assign({}, this.localFilters[key] || {});
      next[side] = null;
      this.$set(this.localFilters, key, next);
    },

    removeFilter(key) {
      const def = this.schema.find(s => s.key === key);
      if (!def) return;
      if (def.type === "daterange") {
        this.$set(this.localFilters, key, { from: null, to: null });
      } else if (def.type === "multiselect") {
        this.$set(this.localFilters, key, []);
      } else {
        this.$set(this.localFilters, key, null);
      }
      this.onApply();
    },

    onReset() {
      const cleared = {};
      this.schema.forEach(s => {
        if (s.type === "daterange") cleared[s.key] = { from: null, to: null };
        else if (s.type === "multiselect") cleared[s.key] = [];
        else cleared[s.key] = null;
      });
      this.localFilters = cleared;
      this.onApply();
    },

    onApply() {
      const normalized = this.normalizeFilters(this.localFilters);
      this.$emit("apply", { filters: normalized, chips: this.activeChips });
    },

    normalizeFilters(filters) {
      const out = {};
      for (const [k, v] of Object.entries(filters || {})) {
        if (v === null || v === "" || v === undefined) continue;
        if (Array.isArray(v) && !v.length) continue;
        if (typeof v === "object" && v && "from" in v && "to" in v && !v.from && !v.to) continue;
        out[k] = v;
      }
      return out;
    },

    updateField(key, v) {
      this.$set(this.localFilters, key, v);
    },
    syncFromProp() {
      this.isSyncing = true;

      const src = this.value || {};
      const nextLocal = JSON.parse(JSON.stringify(src));

      this.schema
        .filter(s => s.type === "daterange")
        .forEach(s => {
          if (!nextLocal[s.key]) nextLocal[s.key] = { from: null, to: null };
          else {
            if (!("from" in nextLocal[s.key])) nextLocal[s.key].from = null;
            if (!("to" in nextLocal[s.key])) nextLocal[s.key].to = null;
          }
        });

      this.localFilters = nextLocal;

      this.schema
        .filter(s => s.type === "daterange")
        .forEach(s => {
          if (!this.rangesMenu[s.key]) this.$set(this.rangesMenu, s.key, { from: false, to: false });
          const curr = this.localFilters[s.key];
          if (!this.tempRange[s.key]) {
            this.$set(this.tempRange, s.key, { from: curr.from, to: curr.to });
          } else {
            if (!("from" in this.tempRange[s.key])) this.$set(this.tempRange[s.key], "from", curr.from);
            else this.tempRange[s.key].from = curr.from;

            if (!("to" in this.tempRange[s.key])) this.$set(this.tempRange[s.key], "to", curr.to);
            else this.tempRange[s.key].to = curr.to;
          }
        });

      this.isSyncing = false;
    }
  }
};
</script>
